/**
 * T9-017 — Search results dropdown.
 *
 * Stateless presentation; the parent (GlobalSearchBar) owns the focused
 * index + result list and passes them in. Single-column on mobile;
 * the dropdown spans the input width on desktop.
 */

export interface SearchResultRow {
  entityType: 'account' | 'contact' | 'deal' | 'activity';
  id: string;
  displayName: string;
  snippet: string;
  rank: number;
  accountId?: string;
}

/**
 * RIG-478 — every result type now has a destination, and a contact or activity
 * lands on the TAB that holds it rather than the account overview.
 *
 * Previously a contact or activity whose `accountId` was missing produced
 * `null`, `navigateToResult` did nothing with it, and `SearchResults` still
 * rendered the row as an ordinary clickable option. The user searched, saw
 * their contact, clicked it, watched the dropdown close, and nothing happened.
 * Rows that genuinely cannot be opened are now rendered non-interactive with a
 * reason, so a click that does nothing is never offered.
 */
export function hrefForResult(result: SearchResultRow): string | null {
  switch (result.entityType) {
    case 'account':
      return `/accounts/${result.id}`;
    case 'contact':
      return result.accountId ? `/accounts/${result.accountId}/contacts` : null;
    case 'deal':
      return `/deals/${result.id}`;
    case 'activity':
      return result.accountId ? `/accounts/${result.accountId}/activities` : null;
    default:
      return null;
  }
}

const ENTITY_LABEL: Record<SearchResultRow['entityType'], string> = {
  account: 'Account',
  contact: 'Contact',
  deal: 'Deal',
  activity: 'Activity',
};

// Phase 4.6 Standard §5.6 — informational badges use a tinted shade of
// the semantic state color, not raw Tailwind hexes. Token-references
// react to dark mode automatically and stay aligned across surfaces.
const ENTITY_BADGE_COLOR: Record<SearchResultRow['entityType'], string> = {
  account: 'bg-rr-teal-light/20 text-rr-teal-dark',
  contact:
    'bg-[color-mix(in_srgb,var(--rr-state-info)_15%,transparent)] text-[var(--rr-state-info)]',
  deal: 'bg-[var(--rr-badge-attention-bg)] text-[var(--rr-badge-attention-text)]',
  activity: 'bg-[var(--rr-badge-neutral-bg)] text-[var(--rr-badge-neutral-text)]',
};

export interface SearchResultsProps {
  results: ReadonlyArray<SearchResultRow>;
  loading: boolean;
  error: string | null;
  focusedIndex: number;
  onPick: (r: SearchResultRow) => void;
}

export function SearchResults({
  results,
  loading,
  error,
  focusedIndex,
  onPick,
}: SearchResultsProps): JSX.Element {
  return (
    <div
      role="listbox"
      className="absolute top-full mt-1 left-0 right-0 z-50 rounded-md border border-rr-border-default bg-rr-surface shadow-lg max-h-[60vh] overflow-y-auto"
    >
      {loading && results.length === 0 && (
        <div className="px-3 py-2 text-sm text-rr-fg-secondary">Searching…</div>
      )}
      {error !== null && (
        <div className="px-3 py-2 text-sm text-rr-error">{error}</div>
      )}
      {!loading && !error && results.length === 0 && (
        <div className="px-3 py-2 text-sm text-rr-fg-secondary">No results.</div>
      )}
      {/* RIG-478 — a row with no destination is rendered non-interactive with
          the reason, instead of looking exactly like a navigable one and doing
          nothing when clicked. */}
      {results.map((r, i) => {
        const openable = hrefForResult(r) !== null;
        return (
          <button
            key={`${r.entityType}-${r.id}`}
            type="button"
            role="option"
            aria-selected={i === focusedIndex}
            aria-disabled={!openable}
            disabled={!openable}
            onClick={() => onPick(r)}
            data-openable={openable ? 'true' : 'false'}
            className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm ${
              openable
                ? 'hover:bg-rr-surface-raised'
                : 'cursor-not-allowed opacity-60'
            } ${i === focusedIndex && openable ? 'bg-rr-surface-raised' : ''}`}
          >
            <span
              className={`inline-block flex-shrink-0 rounded-md px-2 py-0.5 text-xs font-medium ${ENTITY_BADGE_COLOR[r.entityType]}`}
            >
              {ENTITY_LABEL[r.entityType]}
            </span>
            <span className="flex-1 min-w-0">
              {/* QA 2026-10-01 F-31: long unbroken names and snippets wrap
                  (two lines at most) instead of running off the panel. */}
              <span className="block font-medium wrap-anywhere line-clamp-2" title={r.displayName}>
                {r.displayName}
              </span>
              {r.snippet && (
                <span className="block text-xs text-rr-fg-secondary wrap-anywhere line-clamp-2">
                  {r.snippet}
                </span>
              )}
              {!openable && (
                <span className="block text-xs text-rr-fg-secondary">
                  Not linked to an account, so there is nowhere to open it.
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
